/**
 * @fileoverview Registry for authored SVG artwork bodies by stable ID.
 * @module ui-artwork/svg
 *
 * Responsibilities:
 * - Combine the four SVG family maps under their persisted catalog IDs.
 * - Expose a resolver that distinguishes missing artwork from glyph entries.
 *
 * Dependencies: Family-specific local SVG maps.
 * Architecture: Static lookup registry; no runtime fallback asset source.
 * Notes: Every registered non-glyph ID must have exactly one body renderer.
 */
import { BADGE_SVG_BODIES } from './badges';
import { GAP_3D_SVG_BODIES } from './gap3d';
import { OPERATIONAL_SVG_BODIES } from './operational';
import { THREE_D_SVG_BODIES } from './threeD';

/**
 * @contract AUTHORED_SVG_BODIES
 * Purpose: Registers every stable non-glyph catalog ID to its authored renderer.
 * Fields: Stable artwork ID keys and color-aware SVG body functions.
 * Rules: Every non-glyph catalog ID must resolve; absence is an error at build time.
 * Sync: Static bundled source, with no remote asset dependency.
 */
export const AUTHORED_SVG_BODIES = {
  ...THREE_D_SVG_BODIES,
  ...GAP_3D_SVG_BODIES,
  ...OPERATIONAL_SVG_BODIES,
  ...BADGE_SVG_BODIES,
};

/**
 * @reusable getAuthoredSvgBody
 * Usage: Resolve a locally authored body before building a non-glyph SVG.
 * Rules: Returns undefined for glyphs and missing IDs; callers must fail closed for missing non-glyphs.
 */
export function getAuthoredSvgBody(id: string): ((color: string) => string) | undefined {
  return AUTHORED_SVG_BODIES[id as keyof typeof AUTHORED_SVG_BODIES];
}
